/**
 * F0 — Central catalog-driven WBS stage constant.
 *
 * Resolves WBS stage data from `GET /api/wbs/stages` at runtime.
 * NEVER use numeric ids or hardcoded label strings. Resolve by `typeName`
 * (stable machine key from §A seed) and by `scope` (FLOOR|BUILDING|PROJECT|UNIT).
 *
 * Exported:
 *   useWbsStages()            — hook; fetches + caches catalog; returns { stages, byId, byTypeName, loading, error }
 *   WBS_TYPE                  — stable typeName string map (no ids)
 *   WBS_SCOPE                 — scope vocabulary
 *   isSlab(stage)             — true when stage.typeName === WBS_TYPE.SLAB
 *   isPlinth(stage)           — true when stage.typeName === WBS_TYPE.PLINTH
 *   isFloorScoped(stage)      — true when stage.scope === WBS_SCOPE.FLOOR
 *   hasMedia(stage)           — true when !!stage?.hasMedia
 *   resolveStageByName(byTypeName, typeName) — returns live WbsStageData for a typeName key
 */

import { useState, useEffect } from "react";
import axiosInstance from "../services/api/axios";

// ---------------------------------------------------------------------------
// Stable machine-key map (used for name resolution ONLY — never for id comparisons)
// ---------------------------------------------------------------------------
export const WBS_TYPE = Object.freeze({
  PLINTH: "PLINTH",
  SLAB: "SLAB",
  MASONRY_INTERNAL_PLASTER: "MASONRY_INTERNAL_PLASTER",
  FLOORING_TILING: "FLOORING_TILING",
  DOORS_WINDOWS: "DOORS_WINDOWS",
  SANITARY_PLUMBING_INTERNAL: "SANITARY_PLUMBING_INTERNAL",
  STAIRCASE_LIFTWELL_LOBBY: "STAIRCASE_LIFTWELL_LOBBY",
  EXTERNAL_PLASTER_ELEVATION: "EXTERNAL_PLASTER_ELEVATION",
  EXTERNAL_PLUMBING_TERRACE_WP: "EXTERNAL_PLUMBING_TERRACE_WP",
  LIFTS_COMMISSIONING: "LIFTS_COMMISSIONING",
  WATER_PUMPS_TANKS: "WATER_PUMPS_TANKS",
  ELECTRICAL_PANELS_FITTINGS: "ELECTRICAL_PANELS_FITTINGS",
  INTERNAL_PAINTING: "INTERNAL_PAINTING",
  ELECTRICAL_FIRST_FIX: "ELECTRICAL_FIRST_FIX",
  PLUMBING_FIRST_FIX: "PLUMBING_FIRST_FIX",
  WATERPROOFING: "WATERPROOFING",
  COMMON_AREA_FINISHES: "COMMON_AREA_FINISHES",
  HANDOVER_POSSESSION: "HANDOVER_POSSESSION",
  POSSESSION_READY: "POSSESSION_READY",
  CUSTOM: "CUSTOM",
});

// ---------------------------------------------------------------------------
// Scope vocabulary — TOWER was merged into BUILDING (user decision 2026-06-06)
// ---------------------------------------------------------------------------
export const WBS_SCOPE = Object.freeze({
  FLOOR: "FLOOR",
  BUILDING: "BUILDING",
  PROJECT: "PROJECT",
  UNIT: "UNIT",
});

// ---------------------------------------------------------------------------
// Module-level promise memo — fetches once per JS module lifetime.
// Re-sets on 401 (forceLogout event) so a new session gets a fresh fetch.
// ---------------------------------------------------------------------------
let _catalogPromise = null;
let _catalogData = null;

const _resetCache = () => {
  _catalogPromise = null;
  _catalogData = null;
};

if (typeof window !== "undefined") {
  window.addEventListener("forceLogout", _resetCache);
}

const _fetchCatalog = () => {
  if (_catalogPromise) return _catalogPromise;
  _catalogPromise = axiosInstance
    .get("/wbs/stages", { params: { isActive: true } })
    .then((data) => {
      const stages = Array.isArray(data) ? data : data?.items || [];
      _catalogData = stages;
      return stages;
    })
    .catch((err) => {
      // On error reset so next mount retries
      _catalogPromise = null;
      _catalogData = null;
      throw err;
    });
  return _catalogPromise;
};

// ---------------------------------------------------------------------------
// useWbsStages hook
// ---------------------------------------------------------------------------
/**
 * Fetches the WBS stage catalog once (module-level memo) and returns:
 *   stages     — WbsStageData[] ordered by sequence
 *   byId       — { [wbsStageId]: WbsStageData }
 *   byTypeName — { [typeName]: WbsStageData }
 *   loading    — boolean
 *   error      — Error | null
 */
export function useWbsStages() {
  const [stages, setStages] = useState(_catalogData || []);
  const [loading, setLoading] = useState(!_catalogData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_catalogData) {
      setStages(_catalogData);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    _fetchCatalog()
      .then((data) => {
        if (!cancelled) {
          setStages(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byId = {};
  const byTypeName = {};
  for (const stage of stages) {
    byId[stage.wbsStageId] = stage;
    byTypeName[stage.typeName] = stage;
  }

  return { stages, byId, byTypeName, loading, error };
}

// ---------------------------------------------------------------------------
// Predicates — read server data, NEVER a literal id
// ---------------------------------------------------------------------------

/** True when the stage is SLAB (resolved by typeName, not by id). */
export const isSlab = (stage) => stage?.typeName === WBS_TYPE.SLAB;

/** True when the stage is PLINTH (resolved by typeName, not by id). */
export const isPlinth = (stage) => stage?.typeName === WBS_TYPE.PLINTH;

/** True when the stage is FLOOR-scoped (drives floorTarget / per-floor form fields). */
export const isFloorScoped = (stage) => stage?.scope === WBS_SCOPE.FLOOR;

/** True when the stage supports media uploads (replaces hardcoded allow-array). */
export const hasMedia = (stage) => !!stage?.hasMedia;

// ---------------------------------------------------------------------------
// resolveStageByName — returns the live WbsStageData from a fetched byTypeName map
// ---------------------------------------------------------------------------
/**
 * Resolves the live WbsStageData for a given typeName from the catalog map.
 * Usage: resolveStageByName(byTypeName, WBS_TYPE.SLAB) → WbsStageData | undefined
 *
 * This is how code that previously used statusTypeId===2 should look up
 * SLAB — at runtime, from the server-fetched catalog.
 */
export const resolveStageByName = (byTypeName, typeName) =>
  byTypeName ? byTypeName[typeName] : undefined;
